<template>
    <div>
        <!-- Hero Section -->
        <v-container
            fluid
            class="hero-section d-flex pa-0"
        >
            <v-col
                cols="12"
                class="d-flex align-center justify-center text-center"
            >
                <div class="hero-content pa-8">
                    <h1 class="display-1 font-weight-bold text-primary mb-4">
                        Simple, Transparent Pricing
                    </h1>
                    <p class="text-h6 text-grey-darken-1 mb-6">
                        Pay only for the students you teach. Start with a free
                        trial, then choose the plan that fits your studio size.
                    </p>
                    <v-chip
                        color="success"
                        size="large"
                        class="font-weight-bold"
                    >
                        <v-icon
                            start
                            :icon="mdiCheckCircle"
                        />
                        30-Day Free Trial
                    </v-chip>
                </div>
            </v-col>
        </v-container>

        <!-- Pricing Table Section -->
        <v-container class="py-12">
            <v-row>
                <v-col cols="12">
                    <h2 class="text-h4 text-center mb-2 text-primary">
                        Simple Capacity-Based Pricing
                    </h2>
                    <p class="text-h6 text-center text-grey-darken-1 mb-8">
                        Pay only for the students you teach
                    </p>
                </v-col>
            </v-row>
            <v-row justify="center">
                <v-col
                    cols="12"
                    md="10"
                    lg="8"
                >
                    <v-card elevation="4">
                        <v-card-title class="pa-6 text-center">
                            <h3 class="text-h5 text-primary">
                                Annual Subscription Tiers
                            </h3>
                        </v-card-title>
                        <v-card-text class="pa-0">
                            <v-table>
                                <thead>
                                    <tr>
                                        <th
                                            class="text-left font-weight-bold text-h6 pa-4"
                                        >
                                            Students
                                        </th>
                                        <th
                                            class="text-center font-weight-bold text-h6 pa-4"
                                        >
                                            Annual Price
                                        </th>
                                        <th
                                            class="text-center font-weight-bold text-h6 pa-4"
                                        >
                                            Per Student
                                        </th>
                                        <th
                                            class="text-center font-weight-bold text-h6 pa-4"
                                        >
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(tier, index) in pricingTiers"
                                        :key="index"
                                        :class="
                                            tier.popular
                                                ? 'bg-blue-lighten-5'
                                                : ''
                                        "
                                    >
                                        <td class="pa-4">
                                            <div class="d-flex align-center">
                                                <span
                                                    class="text-h6 font-weight-bold mr-2"
                                                >
                                                    Up to {{ tier.students }}
                                                </span>
                                                <v-chip
                                                    v-if="tier.popular"
                                                    color="primary"
                                                    size="small"
                                                >
                                                    Most Popular
                                                </v-chip>
                                            </div>
                                            <p
                                                class="text-body-2 text-grey-darken-1 mt-1"
                                            >
                                                {{ tier.description }}
                                            </p>
                                        </td>
                                        <td class="text-center pa-4">
                                            <span
                                                class="text-h4 font-weight-bold text-primary"
                                            >
                                                ${{ tier.price }}
                                            </span>
                                            <span
                                                class="text-body-1 text-grey-darken-1"
                                            >
                                                /year
                                            </span>
                                        </td>
                                        <td class="text-center pa-4">
                                            <span
                                                class="text-body-1 text-grey-darken-1"
                                            >
                                                ${{
                                                    (
                                                        tier.price /
                                                        tier.students
                                                    ).toFixed(2)
                                                }}
                                            </span>
                                        </td>
                                        <td class="text-center pa-4">
                                            <v-btn
                                                color="primary"
                                                :variant="
                                                    tier.popular
                                                        ? 'flat'
                                                        : 'outlined'
                                                "
                                                class="font-weight-bold"
                                                :to="
                                                    store.hasFeature(
                                                        'self-signup',
                                                    )
                                                        ? '/signup'
                                                        : undefined
                                                "
                                                @click="
                                                    !store.hasFeature(
                                                        'self-signup',
                                                    ) && (waitlistModal = true)
                                                "
                                            >
                                                {{
                                                    store.hasFeature(
                                                        'self-signup',
                                                    )
                                                        ? 'Start Trial'
                                                        : 'Join Waitlist'
                                                }}
                                            </v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-card-text>
                        <v-card-actions class="pa-6 pt-0 justify-center">
                            <v-alert
                                type="info"
                                variant="tonal"
                                class="ma-0"
                                width="100%"
                            >
                                <strong>Overage Pricing:</strong> Need a few
                                extra students? Pay just $5/year for each
                                additional student beyond your tier limit.
                            </v-alert>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- Value Proposition Section -->
        <v-container
            fluid
            class="grey-lighten-5 py-12"
        >
            <v-container>
                <v-row>
                    <v-col
                        cols="12"
                        md="8"
                        class="mx-auto text-center"
                    >
                        <h2 class="text-h4 text-primary mb-4">
                            Why Capacity-Based Pricing?
                        </h2>
                        <p class="text-h6 text-grey-darken-1 mb-6">
                            Fair, transparent pricing that scales with your
                            studio
                        </p>
                        <v-row>
                            <v-col
                                cols="12"
                                md="4"
                                class="mb-4"
                            >
                                <v-icon
                                    :icon="mdiScaleBalance"
                                    size="48"
                                    color="primary"
                                    class="mb-3"
                                />
                                <h3 class="text-h6 mb-2">Pay What You Use</h3>
                                <p class="text-body-2 text-grey-darken-1">
                                    Only pay for active students, not arbitrary
                                    feature limits
                                </p>
                            </v-col>
                            <v-col
                                cols="12"
                                md="4"
                                class="mb-4"
                            >
                                <v-icon
                                    :icon="mdiTrendingUp"
                                    size="48"
                                    color="primary"
                                    class="mb-3"
                                />
                                <h3 class="text-h6 mb-2">Grow Naturally</h3>
                                <p class="text-body-2 text-grey-darken-1">
                                    Pricing scales smoothly as your studio
                                    expands
                                </p>
                            </v-col>
                            <v-col
                                cols="12"
                                md="4"
                                class="mb-4"
                            >
                                <v-icon
                                    :icon="mdiCheckCircle"
                                    size="48"
                                    color="primary"
                                    class="mb-3"
                                />
                                <h3 class="text-h6 mb-2">
                                    All Features Included
                                </h3>
                                <p class="text-body-2 text-grey-darken-1">
                                    No feature tiers - get everything regardless
                                    of plan
                                </p>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- What's Included Section -->
        <v-container class="py-12">
            <v-row>
                <v-col cols="12">
                    <h2 class="text-h4 text-center mb-2 text-primary">
                        Everything Included in All Plans
                    </h2>
                    <p class="text-h6 text-center text-grey-darken-1 mb-8">
                        All the tools you need to transform your studio
                        scheduling
                    </p>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                    md="8"
                    class="mx-auto"
                >
                    <v-row>
                        <v-col
                            v-for="(feature, index) in includedFeatures"
                            :key="index"
                            cols="12"
                            sm="6"
                            class="mb-4"
                        >
                            <div class="d-flex align-start">
                                <v-icon
                                    :icon="mdiCheckCircle"
                                    color="success"
                                    class="mr-3 mt-1"
                                    size="20"
                                />
                                <div>
                                    <h4
                                        class="text-body-1 font-weight-bold mb-1"
                                    >
                                        {{ feature.title }}
                                    </h4>
                                    <p class="text-body-2 text-grey-darken-1">
                                        {{ feature.description }}
                                    </p>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-container>

        <!-- Features Details Section -->
        <v-container
            fluid
            class="grey-lighten-5 py-12"
        >
            <v-container>
                <v-row>
                    <v-col cols="12">
                        <h2 class="text-h4 text-center mb-8 text-primary">
                            How Studoku Works
                        </h2>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        v-for="(feature, index) in detailedFeatures"
                        :key="index"
                        cols="12"
                        md="6"
                        lg="4"
                        class="mb-6"
                    >
                        <div class="d-flex align-start">
                            <v-icon
                                :icon="feature.icon"
                                :color="feature.color"
                                class="mr-4 mt-1"
                                size="32"
                            />
                            <div>
                                <h3 class="text-h6 mb-2">
                                    {{ feature.title }}
                                </h3>
                                <p class="text-body-2 text-grey-darken-1">
                                    {{ feature.description }}
                                </p>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- FAQ Section -->
        <v-container
            fluid
            class="grey-lighten-5 py-12"
        >
            <v-container>
                <v-row>
                    <v-col cols="12">
                        <h2 class="text-h4 text-center mb-8 text-primary">
                            Frequently Asked Questions
                        </h2>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        cols="12"
                        md="8"
                        class="mx-auto"
                    >
                        <v-expansion-panels
                            variant="accordion"
                            multiple
                        >
                            <v-expansion-panel
                                v-for="(faq, index) in faqs"
                                :key="index"
                                :title="faq.question"
                            >
                                <v-expansion-panel-text>
                                    <p class="text-body-1">
                                        {{ faq.answer }}
                                    </p>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- CTA Section -->
        <v-container
            fluid
            class="bg-primary py-12"
        >
            <v-container>
                <v-row>
                    <v-col
                        cols="12"
                        class="text-center"
                    >
                        <h2 class="text-h4 text-white font-weight-bold mb-3">
                            Ready to Transform Your Studio?
                        </h2>
                        <div v-if="store.hasFeature('self-signup')">
                            <p
                                class="text-h6 text-white text-opacity-90 font-weight-regular mb-6"
                            >
                                Start your 30-day free trial today. No credit
                                card required.
                            </p>
                            <v-btn
                                color="yellow-darken-2"
                                size="large"
                                class="font-weight-bold mb-3"
                                to="/signup"
                            >
                                Start Free Trial
                                <v-icon
                                    end
                                    :icon="mdiArrowRight"
                                />
                            </v-btn>
                            <p class="text-caption text-white text-opacity-70">
                                Try all features free for 30 days
                            </p>
                        </div>
                        <div v-else>
                            <p
                                class="text-h6 text-white text-opacity-90 font-weight-regular mb-6"
                            >
                                Join our waitlist and be first to try Studoku
                                when we launch.
                            </p>
                            <v-btn
                                color="yellow-darken-2"
                                size="large"
                                class="font-weight-bold mb-3"
                                @click="waitlistModal = true"
                            >
                                Join Waitlist
                                <v-icon
                                    end
                                    :icon="mdiArrowRight"
                                />
                            </v-btn>
                            <p class="text-caption text-white text-opacity-70">
                                Get notified when pricing launches
                            </p>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- Waitlist Dialog Component -->
        <WaitlistModal
            v-if="!store.hasFeature('self-signup')"
            v-model="waitlistModal"
        />
    </div>
</template>

<script lang="ts" setup>
import {
    mdiAccountGroup,
    mdiArrowRight,
    mdiCheckCircle,
    mdiClipboardList,
    mdiClockFast,
    mdiHumanGreetingVariant,
    mdiRefresh,
    mdiScaleBalance,
    mdiTarget,
    mdiTrendingUp,
} from '@mdi/js';
import { ref } from 'vue';

import WaitlistModal from '@/shared/components/modals/WaitlistModal.vue';
import { useAppStore } from '@/shared/store';

const store = useAppStore();
const waitlistModal = ref(false);

const pricingTiers = [
    {
        description: 'Perfect for new or small studios',
        price: 20,
        students: 15,
        popular: false,
    },
    {
        description: 'Ideal for growing studios',
        price: 35,
        students: 30,
        popular: true,
    },
    {
        description: 'For established studios',
        price: 60,
        students: 60,
        popular: false,
    },
];

const includedFeatures = [
    {
        title: 'Automated Schedule Generation',
        description:
            'Create optimized schedules in minutes instead of days or weeks',
    },
    {
        title: 'Student Preference Collection',
        description: 'Gather student preferences through simple survey system',
    },
    {
        title: 'Conflict Minimization',
        description: 'Maximize satisfaction and minimize scheduling conflicts',
    },
    {
        title: 'Fresh Start Optimization',
        description: 'Regular fresh starts prevent schedule baggage buildup',
    },
    {
        title: 'Teacher Availability Priority',
        description: 'Respect your optimal teaching hours and personal time',
    },
    {
        title: 'Fair Time Distribution',
        description:
            'Every student gets a fair chance at their ideal time slot',
    },
];

const detailedFeatures = [
    {
        title: 'Time-Saving Automation',
        description:
            'Create perfect studio schedules in minutes instead of days or weeks. Studoku eliminates manual scheduling headaches.',
        icon: mdiClockFast,
        color: 'blue',
    },
    {
        title: 'Student Preference Collection',
        description:
            "Gather each student's preferred times and scheduling constraints through our simple survey system.",
        icon: mdiClipboardList,
        color: 'green',
    },
    {
        title: 'Optimization Algorithm',
        description:
            'Our algorithm starts with a clean slate and optimizes the entire schedule to give the most students their best times.',
        icon: mdiTarget,
        color: 'orange',
    },
    {
        title: 'Student Retention',
        description:
            "Accommodate your students' scheduling needs. Prevent dropouts due to time conflicts that frustrate families.",
        icon: mdiAccountGroup,
        color: 'purple',
    },
    {
        title: 'Teacher Wellbeing',
        description:
            'Teach during your optimal hours. No more sacrificing your evenings, early mornings, or mealtimes.',
        icon: mdiHumanGreetingVariant,
        color: 'red',
    },
    {
        title: 'Fresh Start Scheduling',
        description:
            'Start fresh each scheduling period as circumstances and preferences change over time.',
        icon: mdiRefresh,
        color: 'teal',
    },
];

const faqs = [
    {
        question: 'What counts as an "active student"?',
        answer: "An active student is someone currently enrolled in lessons with you. Students on extended breaks or who have stopped lessons don't count toward your limit.",
    },
    {
        question: 'Can I change plans anytime?',
        answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and billing is prorated accordingly.',
    },
    {
        question: 'What happens if I exceed my student limit?',
        answer: 'You can temporarily exceed your limit using our overage pricing at $5 per additional student per year. We recommend upgrading if you consistently need more capacity.',
    },
    {
        question: 'Is there a setup fee?',
        answer: 'No setup fees, ever. You only pay the annual subscription for your chosen plan, plus any overage students if applicable.',
    },
    {
        question: "What's included in the free trial?",
        answer: 'The 30-day free trial includes full access to all features with up to 15 students. No credit card required to start.',
    },
    {
        question: 'Do you offer refunds?',
        answer: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied within the first 30 days after your trial, we'll refund your subscription.",
    },
    {
        question: 'Can I get help setting up my studio?',
        answer: 'Absolutely! All plans include email support, and Professional plan users get priority support to help you get started quickly.',
    },
];
</script>

<style scoped>
.hero-section {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    min-height: 500px;
}

.hero-content {
    max-width: 800px;
}
</style>
